import { useCallback, useRef, useState, type MouseEvent } from 'react'

/**
 * Options for {@link useAsyncAction}.
 *
 * @example Automatic pending (async onClick)
 * ```tsx
 * const { isBusy, handleClick } = useAsyncAction({ onClick: approveCallback })
 * ```
 *
 * @example Additive external pending (e.g. Redux ApprovalState.PENDING after hash)
 * ```tsx
 * const { isBusy, handleClick } = useAsyncAction({
 *   onClick: approveCallback,
 *   pending: approval === ApprovalState.PENDING,
 * })
 * ```
 *
 * @example Sync onClick — no internal pending
 * ```tsx
 * const { handleClick } = useAsyncAction({ onClick: () => setShowConfirm(true) })
 * ```
 */
export type UseAsyncActionOptions = {
  onClick?: () => void | Promise<void>
  disabled?: boolean
  /** External busy flag; combined with internal async pending via OR. */
  pending?: boolean
}

export type UseAsyncActionResult = {
  isBusy: boolean
  handleClick: (e: MouseEvent) => void
  /** Clears internal async pending only; does not affect external `pending` prop. */
  reset: () => void
}

function isPromise(value: unknown): value is Promise<void> {
  return (
    value !== null &&
    typeof value === 'object' &&
    'then' in value &&
    typeof (value as Promise<void>).then === 'function'
  )
}

export function useAsyncAction({
  onClick,
  disabled = false,
  pending = false,
}: UseAsyncActionOptions): UseAsyncActionResult {
  const [internalPending, setInternalPending] = useState(false)
  const internalPendingRef = useRef(false)
  const isBusy = internalPending || Boolean(pending)

  const reset = useCallback(() => {
    internalPendingRef.current = false
    setInternalPending(false)
  }, [])

  const handleClick = useCallback(
    (e: MouseEvent) => {
      if (disabled || internalPendingRef.current || Boolean(pending) || !onClick) return

      const result = onClick()
      if (!isPromise(result)) return

      internalPendingRef.current = true
      setInternalPending(true)
      void result
        .catch(() => {
          // Caller surfaces errors; keep pending UX until settled.
        })
        .finally(() => {
          internalPendingRef.current = false
          setInternalPending(false)
        })
    },
    [disabled, pending, onClick],
  )

  return { isBusy, handleClick, reset }
}
